
import React, { useMemo, useCallback, createContext, ReactNode, useContext } from 'react';
import { DataItemProps } from '../';
import { convertToZonedTimestamp } from 'common/helpers/dateAndTimeHelpersV2';
import { typeChecker } from 'common/helpers/typeChecker';
import { valueKeyTotals } from '../GeneratorCharts/helpers/getValueKeyTotals';
 
export interface UseDataProviderProps {
  filter?: (x?: DataItemProps & Record<string, unknown>) => boolean
  editItem?: (x?: DataItemProps & Record<string, unknown>) => DataItemProps
  colors?: Record<string, string>,
  data?: DataItemProps[],
  cachedData?: DataItemProps[],
  chartData?: DataItemProps[],
  isLoading?: boolean,
  error?: DataItemProps,
  isFetching?: boolean,
  hasError?: ReactNode,
  hasMessage?: ReactNode,
  waitForData?: boolean,
  mapKeys?: Record<string, string>,
  children?: ReactNode,
  key?: keyof DataItemProps,
  /** keys is an array that groups and totals the data based on the order of the keys in this array */
  keys?:(keyof DataItemProps)[],
  groupKey?: keyof DataItemProps,
  /** the key used to get item count, otherwise it will use the occurence */
  countKey?: keyof DataItemProps,
  categoryKey?: keyof DataItemProps,
  valueKey?: keyof DataItemProps,
  valueKeys?: (keyof DataItemProps)[],
  timestampKey?: keyof DataItemProps,
  startKey?: keyof DataItemProps,
  endKey?: keyof DataItemProps,
  sortKey?: keyof DataItemProps,
  sortOrder?: string | 'asc' | 'desc',
  /** this will map old keys to new keys, rename them and delete the old key */
  renameKeys?: {
    [key: keyof DataItemProps]: string & keyof DataItemProps
  }

  /** the following is only used in the handler */
  /** attached the index locations in the origional data set for the items used for grouping */
  attachIndexes?: boolean,
  /** attached the items used for grouping */
  attachItems?: boolean,
  /** returns a flattened array with info on what was used to make the new data set */
  flatten?: boolean,
  /** converts string dates to number dates */
  numberDates?: boolean,
  /** add keys to grouping totals, otherwise only the keys used for calculations are included */
  includeKeys?: (keyof DataItemProps)[],
  timeGroup?: '15 minutes' | '30 minutes' | 'hour' | 'day' | 'week' | 'month' | 'year' | '10 minutes' | '5 minutes' | string | number,
  timeZone?: string

}
 
export interface GroupTotalItemProps {
  count: number
  total?: number
  average?: number
  low?: number
  high?: number
  /** an array of items from the data that made up this group total */
  items?: DataItemProps[]
  /** the locations of the data item that were used to ge these totals */
  indexes?: number[]
  valueKey?: string
  value?: number,
  percent?: number,
  allTotals?: number,
  /** the data index of the first item in this group */
  first?: number,
  /** the data index of the last item in this group */
  last?: number,
  [key: string]: unknown
}

export interface GroupTotalsProps {
  [key: string]: GroupTotalItemProps
}

export interface CategoryTotalsProps {
  [key: string]: GroupTotalsProps
}

export interface GroupedDataProps {
  [key: string]: DataItemProps[]
}

export type KeyValsProps = string[]

export type GetDataReturnProps = GroupedDataProps | GroupTotalItemProps[] | CategoryTotalsProps | GroupTotalsProps | KeyValsProps | DataItemProps[] | DataItemProps | (string | number | undefined)[] |string | number | undefined | void
export type UseDataProviderReturnProps = [
  (type: string, options?: UseDataProviderProps) => GetDataReturnProps,
   { isLoading?: boolean, hasError?: boolean, hasMessage?: boolean } | undefined
]

const DataContext = createContext<UseDataProviderReturnProps>([
  ((type: string) => {
    console.log(type, 'not set');
    return undefined;
  }), 
  undefined
]);

export const groupTotals = ({ data, groupKey, valueKey, attachItems = true, attachIndexes = true, includeKeys, countKey}: UseDataProviderProps): GroupTotalsProps | undefined => data && Array.from(data)?.reduce((acc: GroupTotalsProps, item: DataItemProps, dataIndex: number) => {
 
  if (!groupKey) return acc
  
  const group = String(item?.[groupKey as string] as string || 'unknown')
  const value = valueKey && item?.[valueKey as string] as number | undefined
  const count = Number(countKey ? item?.[countKey] : 1)

  if (!acc?.[group]) acc = {...acc, [group]: {  count: 0, total: 0, average: 0, low: value || 0, high: value || 0, [valueKey as string]: value, [groupKey]: String(group)  }}
  
  if (attachItems) {
    if (!acc[group].items) acc[group].items = []
    acc[group].items = [...acc[group].items, item]
  } else if (attachIndexes) {
    if (!acc[group].items) acc[group].indexes = []
     acc[group].indexes = [...(acc[group].indexes as number[]), dataIndex]
  }

  acc[group].count = acc[group].count + count
  
  if (valueKey) {
 
    if (typeof value === 'number') {
      acc[group].total = (acc[group].total as number) + value
      acc[group].average = (acc[group].total as number) / (acc[group].count as number)
      //acc[group].allTotals = (acc[group].allTotals || 0) + value
      if (value < (acc[group].low as number)) acc[group].low = value
      if (value > (acc[group].high as number)) acc[group].high = value
    }
  }
  
  if (includeKeys) {
    includeKeys?.forEach((key: keyof DataItemProps) => {
      if (item?.[key] && !acc[group]?.[key]) acc[group][key] = String(item?.[key])
    })
  }

  return acc as GroupTotalsProps
  
},{}) as GroupTotalsProps

const categoryTotals = ({ data, groupKey, categoryKey, valueKey,   ...rest}: UseDataProviderProps): CategoryTotalsProps | undefined => {
  if (!data || !groupKey || !categoryKey) return undefined
  let categoryTotals = {}
    Object.entries(Object?.groupBy?.(data, (item) => item?.[categoryKey as string] as string)).forEach(([cat, items]) => {
      categoryTotals = {
        ...categoryTotals,
        [cat]: groupTotals({ data: items as DataItemProps[], groupKey, valueKey, ...rest })
      }
    })
    return categoryTotals as CategoryTotalsProps
}


export const keyGrouper = (
  /** an array of string keys that match up with the data */
 
  keys: (keyof DataItemProps)[] | keyof DataItemProps | string | string[], 
 
  { 
    /** data array of objects that will be regrouped by keys */
    data, 
    /** the key of the data item to get a value from , this will be used to get the total, average, high, low and count values */
    valueKey, 
 
     
 
    /** do you want to attach the data items used for this aggregation */
    attachItems, 
    /** include keys in the totals return, this value should be the same for each group. better used for colors and labels that are the same for each group
    * (example: alarm: blue, info: red ) */
    includeKeys, 
    /** this flattens the data for use with charts, it will return an array with all the settings sent, along with attached items if requested */
    flatten 
  }: UseDataProviderProps): GroupedDataProps | undefined => {
  
  if (!data || !keys) return undefined
  if (typeof keys === 'string') keys = [keys]
 

 

  const groupedObject = Object.entries(
    Object?.groupBy?.(data as DataItemProps[], (item: DataItemProps) =>
      keys.reduce((acc: string, key: keyof DataItemProps) => {
        if (item?.[key]) acc = `${acc}_-_${item?.[key]}`;
        return acc;
      }, '')
    )
  ).reduce((acc: GroupedDataProps, [key, items]) => {
    const keyParts = key.split('_-_').filter((part) => part !== '');
    let currentLevel = acc;
 
    // Build the nested structure
    keyParts.forEach((part, index) => {

      if (!currentLevel[part])  currentLevel[part] = index === keyParts.length - 1 ? { items } : {};
      
      if (index === keyParts.length - 1) {

        currentLevel[part] = {
          ...(currentLevel[part] as GroupedDataProps),
          count: currentLevel[part]?.items?.length || 0
        }

        if (valueKey) {

          currentLevel[part] = {
            ...(currentLevel[part] as GroupedDataProps),
            total: 0,
            low: 0,
            high: 0,
            average: 0,
            valueKey
          }

          items?.forEach((item: DataItemProps) => {
            const value = item?.[valueKey as string] as number | undefined
            if (typeof value === 'number') {
              currentLevel[part].total = (currentLevel[part].total as number) + value
              currentLevel[part].average = (currentLevel[part].total as number) / (currentLevel[part].count as number)
              if (value < (currentLevel[part].low as number)) currentLevel[part].low = value
              if (value > (currentLevel[part].high as number)) currentLevel[part].high = value
            }
          })
        }

        includeKeys?.forEach((key: keyof DataItemProps) => {
          if (items?.[0]?.[key] && !currentLevel[part]?.[key]) currentLevel[part][key] = items?.[0]?.[key]
        })

        if (flatten) {
          const currentKey = keys?.[index] as keyof DataItemProps
          if (!currentLevel[part]?.[currentKey]) currentLevel[part][currentKey] = part
        }

        if (attachItems === false) {
          delete currentLevel[part].items
 
        }
 

      }
      currentLevel = currentLevel[part] as GroupedDataProps;     
    })

    return acc;
  }, {} as GroupedDataProps)
 

 
  return groupedObject as GroupedDataProps
}

export const timeGroupOptions = ['year', 'month', 'week', 'day', 'hour', '30 minutes', '15 minutes', '10 minutes', '5 minutes']

const timeGroups = ({ data, timestampKey, timeGroup, timeZone, numberDates }: UseDataProviderProps): Record<string, DataItemProps[]> | undefined => {

  if (!timestampKey) return undefined
  const type = typeChecker(timestampKey as string)
  if (type?.jsx || !data) return undefined
  
  const groups = Array.from(data)?.reduce((acc: Record<string, DataItemProps[]>, item: DataItemProps) => {
    
    const date = new Date(item?.[timestampKey as string] as string | number | Date).toISOString()
    
    if (!date) return acc
    const offset = typeof date === 'string' && date?.includes('T') && date.split('T')?.[1]
    const hasOffset = offset && (offset?.includes('+') || offset?.includes('-'))
    const autoOffset = hasOffset && date.slice(-6)
    const [yr, mo, wk, da, ho, mi] = (convertToZonedTimestamp(date as string, timeZone, 'yyyy-MM-w-dd-hh-mm') || '').split('-') || []

    let groupId = `` as string | number
    
    switch (timeGroup) {

      case 'year':  { 
        if (!autoOffset) groupId = convertToZonedTimestamp(`${yr}-01-01T00:00:00`, timeZone) as string
        else groupId = `${yr}-01-01T00:00:00${autoOffset}`
        item.timeGroup = groupId
        break
      }
      case 'month': {
        if (!autoOffset) groupId =  convertToZonedTimestamp(`${yr}-${mo}-01T00:00:00`, timeZone) as string
        else groupId = `${yr}-${mo}-01T00:00:00${autoOffset}`
        item.timeGroup = groupId
        break
      }
      case 'week':  { 
        groupId = `${yr}-${wk}`
        break
      }
      case 'day':   { 
        if (!autoOffset) groupId = convertToZonedTimestamp(`${yr}-${mo}-${da}T00:00:00`, timeZone) as string
        else groupId = `${yr}-${mo}-${da}T00:00:00${autoOffset}`
        item.timeGroup = groupId
        break
      }
      case 'hour':  { 
        if (!autoOffset) groupId =  convertToZonedTimestamp(`${yr}-${mo}-${da}T${ho}:00:00`, timeZone) as string
        else groupId = `${yr}-${mo}-${da}T${ho}:00:00${autoOffset}`
        item.timeGroup = groupId
        break
      }
      case '30 minutes': {
        const min30 =  Math.floor(Number(mi) / 30) * 30
        const minStr = min30 < 10 ? `0${min30}` : `${min30}`
        if (!autoOffset) groupId =  convertToZonedTimestamp(`${yr}-${mo}-${da}T${ho}:${minStr}:00`, timeZone) as string
        else groupId = `${yr}-${mo}-${da}T${ho}:${minStr}:00${autoOffset}`
        item.timeGroup = groupId
        break
      }
      case '15 minutes': {
        const min15 =  Math.floor(Number(mi) / 15) * 15
        const minStr = min15 < 10 ? `0${min15}` : `${min15}`
        if (!autoOffset) groupId =  convertToZonedTimestamp(`${yr}-${mo}-${da}T${ho}:${minStr}:00`, timeZone) as string
        else groupId = `${yr}-${mo}-${da}T${ho}:${minStr}:00${autoOffset}`
        item.timeGroup = groupId
        break
      }
      case '10 minutes': {
        const min10 =  Math.floor(Number(mi) / 10) * 10
        const minStr = min10 < 10 ? `0${min10}` : `${min10}`
        if (!autoOffset) groupId =  convertToZonedTimestamp(`${yr}-${mo}-${da}T${ho}:${minStr}:00`, timeZone) as string
        else groupId = `${yr}-${mo}-${da}T${ho}:${minStr}:00${autoOffset}`
        item.timeGroup = groupId
        break
      }
      case '5 minutes': {
        const min5 =  Math.floor(Number(mi) / 5) * 5
        const minStr = min5 < 10 ? `0${min5}` : `${min5}`
        if (!autoOffset) groupId =  convertToZonedTimestamp(`${yr}-${mo}-${da}T${ho}:${minStr}:00`, timeZone) as string
        else groupId = `${yr}-${mo}-${da}T${ho}:${minStr}:00${autoOffset}`
        item.timeGroup = groupId
        break
      }

      default: break
    }
    
    if (numberDates) groupId = new Date(groupId as string).getTime()
    if (!acc?.[groupId]) acc = {...acc, [groupId]: []}

    acc[groupId] = [...acc[groupId], {...item, items: [], timeGroup: groupId}]

    return acc
  }, {})
  return groups
}

export const useDataProvider = (): UseDataProviderReturnProps => useContext(DataContext)

export const getData = (type: string, { 
  flatten = false, 
  attachItems = true, 
  attachIndexes = false, 
  numberDates = false,
  countKey,
  sortKey,
  sortOrder,
  /** group order can be an array of strings as well */
  groupOrder = 'desc', 
  colors,
  chartData,
  cachedData,
  data, 
  key, 
  keys,
  timestampKey,
  categoryKey,
  groupKey, 
  valueKey, 
  startKey, 
  timeGroup, 
  timeZone, 
  valueKeys,
  ignoreKeys,
  renameKeys,
  includeKeys = ['color'],
  ...rest
}: UseDataProviderProps): GetDataReturnProps | undefined => {
  
  if (!data) return undefined
  
  categoryKey = categoryKey || timestampKey
  timestampKey = timestampKey || startKey
  key = key || groupKey  

  const dataArray = Array.from(data) as DataItemProps[]

  switch (type) {
    // returns the original data as sent
    case 'data': return data
    case 'cachedData': return cachedData
    case 'chartData': return chartData

    case 'keyGroups': return keyGrouper(keys as [keyof DataItemProps], { data: dataArray, valueKey, flatten, includeKeys, attachIndexes, attachItems, ...rest }) as GroupedDataProps
    // returns a unique set of values for provided key or groupKey as the default,
    case 'keyVals': return Array.from(new Set(dataArray?.map((item) => item?.[key as string] as string)))
    // groups the data by id and retuns an object with the group key as the key and an array of items as the value
    case 'group': 
    case 'grouped':
      return key && Object?.groupBy?.(dataArray, (item) => item?.[key as string] as string)
    // returns valueKey totals
    case 'valueKeyTotals': {
      const valueKeysTotalsObj = valueKeyTotals({ data: dataArray, valueKey, attachItems, attachIndexes, valueKeys, groupKey, includeKeys, renameKeys }) as GroupTotalsProps
      if (!flatten) return valueKeysTotalsObj
      if (!valueKeysTotalsObj) return undefined
      return Object.values(valueKeysTotalsObj) as GroupTotalItemProps[]
    }
    // returns the data grouped by the groupKey and the valueKey as the total, average, low and high values for each group
    // flatten will send the data back as an array
    case 'groupTotals': {
      const groupTotalsObj = groupTotals({ data: dataArray, groupKey: key,  valueKey, attachItems, attachIndexes, colors, flatten, includeKeys, countKey }) as GroupTotalsProps      
      if (!flatten) return groupTotalsObj

      const flattened = Object.values(groupTotalsObj as GroupTotalsProps)
      let orderedData = ignoreKeys ? flattened?.map((x) => {
        const newItem = {...x}
        ignoreKeys?.forEach((key) => {
          if (newItem?.[key]) delete newItem[key]
        })
        return newItem
      }) : flattened
      
      if (groupOrder) {
        sortKey = sortKey || valueKey ? 'total' : 'count'
 
        if (typeof groupOrder === 'string') {
          if (groupOrder === 'asc') orderedData = [...(flattened || [])].sort((a, b) => {
            return (Number(a?.[sortKey as string]) || 0) - (Number(b?.[sortKey as string]) || 0)
          })
          else if (groupOrder === 'desc') orderedData = [...(flattened || [])].sort((a, b) => {
            return (Number(b?.[sortKey as string]) || 0) - (Number(a?.[sortKey as string]) || 0)
          })
        }
        else if (Array.isArray(groupOrder)) {
          orderedData = [...(flattened|| [])].sort((a, b) => {
            return (groupOrder as string[]).indexOf(String(a?.[groupKey as string])) - (groupOrder as string[]).indexOf(String(b?.[groupKey as string]))
          })
        }
      }
      return orderedData
    }
    // returns the data grouped by the categoryKey and the groupKey as the total, average, low and high values for each group
    // flatten will send the data back as an array with the assigned categories
    case 'categoryTotals': {
       const catTotalsObj = categoryTotals({ data: dataArray, groupKey, categoryKey, countKey, valueKey, attachItems, flatten, attachIndexes, includeKeys }) as CategoryTotalsProps
       if (!flatten) return catTotalsObj
       if (!groupKey || !categoryKey || !catTotalsObj) return undefined
       return Object?.entries(catTotalsObj)?.reduce((acc: GroupTotalItemProps[], [cat, groupItems]) => {
        Object?.values?.(groupItems)?.forEach((item) => {
          acc.push({
            ...item,
            [categoryKey as string]: cat
          })
        })
        return acc
       }, [] as GroupTotalItemProps[])
    }
    
    
  // returns the data grouped by the timestampKey and the timeGroup as the total, average, low and high values for each group
  // flatten will send the data back as an array with the assigned timeGroup as a key/value pair
  case 'timeGroups': {
    const timeGroupsObj = timeGroups({ data: dataArray, timestampKey, timeZone, timeGroup}) as GroupedDataProps
    if (!timeGroup) return undefined
    if (!flatten) return timeGroupsObj
    if (!timeGroupsObj) return undefined

    let flatOne = Object?.entries(timeGroupsObj)?.reduce((acc: DataItemProps[], [group, groupItems]) => {
        Object?.values?.(groupItems)?.forEach((item) => {
          delete item?.indexes
          delete item?.items
          acc.push({
            ...item,
            timeGroup: group
          })
        }) 
        return acc
      }, [])
    
    if (sortOrder) flatOne = flatOne.sort((a, b) => {
      if (sortOrder === 'desc') return new Date(a.timeGroup).getTime() - new Date(b.timeGroup).getTime()
      else return new Date(b.timeGroup).getTime() - new Date(a.timeGroup).getTime()
    })
    return flatOne as DataItemProps[]
  }

  case 'timeTotals': { 
    const flattened =  Object?.entries?.(timeGroups({ data: dataArray, timestampKey, timeZone, timeGroup, numberDates, includeKeys}) || {})?.reduce((acc: DataItemProps[], [group, groupItems]) => {
      Object?.values?.(groupItems)?.forEach((item) => {
        delete item?.indexes
        delete item?.items
        acc.push({
          ...item,
          [timestampKey as string]: group
        })
      }) 
      return acc
    }, [])

  

    return categoryTotals({ data: flattened, groupKey, categoryKey: timestampKey, valueKey, includeKeys, countKey }) as CategoryTotalsProps

  }
  
   default: return 
    
  }

}
export const DataProvider = ({data, isLoading, isFetching, error, waitForData = false, children, ...props}: UseDataProviderProps): JSX.Element => {
 
  const cachedData = useMemo(() => data, [data]) as DataItemProps[]
  const chartData = useMemo(() => {
    if (props?.valueKeys) return getData('valueKeyTotals', {data: cachedData, ...props})
    if (props?.groupKey) {
      if (props?.categoryKey || props?.timestampKey) return getData('categoryTotals', {data: cachedData, ...props})
      return getData('groupTotals', {data: cachedData, ...props})
    }
  },[data]) as DataItemProps[] | undefined
 
  const getter = useCallback((type: string, rest: UseDataProviderProps) => getData(type, {data: cachedData, chartData, ...props, ...rest}),[cachedData])

  return (
    <DataContext.Provider value={[getter, { isLoading, hasError: (error || props?.hasError) ? true : false, hasMessage: (!isFetching && !isLoading && !data) ? true : false }] as UseDataProviderReturnProps}>
      {!waitForData ? children : cachedData ? children : <></>}
    </DataContext.Provider>
  );
};

export default useDataProvider;
